/**
 * Shared Active Spaces pricing for 360 Tours, Planos, ImageMotion, and VideoMotion.
 * Change amounts here; all apps and Stripe products should stay in sync.
 * Slot counts come from here — no Stripe metadata needed for active slots.
 */

// Platform-wide GBP / EUR / USD billing currency, formatter, and detection.
export * from './currency';
export * from './proposed-eur';

// ReHub Studio (studio.rehub.studio) cash pricing — tiers, flat services,
// turnaround surcharges, credit-offset policy, bundles and subscriptions.
export * from './studio';

// Sketchplan direct-card pricing — single source of truth for the app + marketing site.
export * from './sketchplan';

// 360 Tours Active Spaces pricing model (Credits / Standard / Sales Pro / Ultra / Enterprise).
export * from './tours-plans';

// 360Worlds consumer viewing subscription (Full Access monthly / annual) — a
// separate payment axis from Active Spaces (view vs publish).
export * from './worlds-plans';

// HIOZ guest guide plans (published-property limits) + credit costs.
export * from './hioz-plans';

// ImageMotion video packages + one-time double-credit offer.
export * from './imagemotion';

// PropertyCGI Studio — resolution-based generation credits.
export * from './propertycgi';

// VideoMotion Studio — flat video price and optional script credit.
export * from './videomotion';
export * from './propertycut';

// ImageMotion motion-tier profitability helpers (admin).
export * from './imagemotion-motion-tiers';

// Sales dashboard: Stripe amounts in the currency charged.
export * from './recorded-revenue';
export * from './retention-analytics';

// ImageMotion usage pricing (per-clip + compose, margin-protected).
export * from './imagemotion-usage';

// Canonical ReHub credit packs (minor units) + checkout helpers.
export * from './credit-packs';

// v2 new-customer catalogue, credit-action costs, commerce helpers, analytics.
export * from './credit-packs-v2';
export * from './credit-policy-v3';
export * from './credit-actions';
export * from './seo-plans';
export * from './floorplans';
export * from './credit-commerce';
export * from './commerce-events';

// HDRPhoto dedicated monthly image-allowance plans (separate from ReHub Credits).
export * from './hdr-plans';
export * from './hdr-events';

// Photoplan agency residential catalogue (photoplan.ai booking prices).
export * from './photoplan-agency';

// =============================================================================
// Credit expiry policy (shared across every ReHub app + DB)
// =============================================================================

/**
 * Number of months a credit grant remains valid after the grant/purchase date.
 * Update here to change the policy globally — UI strings and the database
 * migration both read from this constant (TS) / from the matching SQL helper
 * `public.credit_policy_expiry_months()`. Keep them in sync.
 */
export const CREDIT_EXPIRY_MONTHS = 12;

/**
 * Single source of truth for the wording we use in the product whenever we
 * mention credit expiry. Components should import these instead of hard-coding
 * any expiry-related copy so policy can be updated globally.
 */
export const CREDIT_EXPIRY_COPY = {
  /** Short tag-line for footers / hero benefits / FAQ summary cards. */
  short: `Credits are valid for ${CREDIT_EXPIRY_MONTHS} months from purchase.`,
  /** Used inside Stripe-checkout/transaction footers. */
  stripeFooter: `Credits are valid for ${CREDIT_EXPIRY_MONTHS} months from purchase.`,
  /** Used in pricing benefits modals. */
  benefitsModal: `Credits are valid for ${CREDIT_EXPIRY_MONTHS} months from purchase. Cancel subscriptions anytime.`,
  /** FAQ-style answer to "Do credits expire?" */
  faqAnswer: `Yes — credits are valid for ${CREDIT_EXPIRY_MONTHS} months from the date of purchase. Unused credits expire ${CREDIT_EXPIRY_MONTHS} months after they are granted, so we recommend using them at a comfortable pace within that window.`,
  /** Inline benefit pill / checkmark wording. */
  benefitPill: `Credits remain active for ${CREDIT_EXPIRY_MONTHS} months`,
  /** Used in legal / terms pages. */
  legal: `Unused credits expire ${CREDIT_EXPIRY_MONTHS} months after the date they are purchased or granted. Subscription credits granted on each monthly billing cycle follow the same ${CREDIT_EXPIRY_MONTHS}-month rolling window.`,
} as const;

/** Format any future expiry policy string from a custom number of months. */
export function formatCreditExpiryCopy(months: number = CREDIT_EXPIRY_MONTHS): string {
  return `Credits are valid for ${months} months from purchase.`;
}

// =============================================================================
// 360 Tours floor plan order credit cost (shared TS source of truth)
// =============================================================================

/**
 * Credits charged for a managed 360 Tours floor plan production order.
 *
 * SINGLE SOURCE OF TRUTH (TS). The database mirrors this value as the default
 * of `public.floorplan_credit_cost()` (backed by `tours_floorplan_settings`),
 * which is authoritative for the actual deduction in `create_floor_plan_order`.
 * The UI imports this constant only so quotes/copy can never drift from the
 * charge — see
 * `supabase/migrations/20260628120000_tours_floor_plan_orders.sql`.
 */
export const TOURS_FLOOR_PLAN_ORDER_CREDITS = 20;

/**
 * ReHub Credits charged to edit one uploaded 360° panorama in 360 Tours.
 *
 * DB mirror: `public.tours_pano_edit_credit_cost()` — keep both at 1.
 * The edit is the panorama-safe Autoenhance pass (light, colour, clarity,
 * privacy blur). See `supabase/migrations/20261013193000_tours_scene_image_edits.sql`.
 */
export const TOURS_PANO_EDIT_CREDITS = 1;

// =============================================================================
// HDR Photo credit cost (shared TS source of truth)
// =============================================================================

/**
 * ReHub Credits charged per completed HDR merge. Always 0 — HDR merge
 * consumes the dedicated HDR image allowance (`hdr_entitlements`), not the
 * shared ReHub Credits wallet. Studio tools that still use ReHub Credits
 * (PhotoClear, staging, HyperDusk, ImageMotion) are billed separately.
 *
 * DB mirror: `public.hdr_credit_cost()` — keep both at 0.
 * See `supabase/migrations/20260911150000_hdr_dedicated_pricing.sql`.
 */
export const HDR_CREDIT_COST = 0;

// =============================================================================
// Property Brochures credit costs (shared TS source of truth)
// =============================================================================

/**
 * Branded brochure creation and PDF downloads are free.
 * Kept at 0 so older "generate" call sites do not charge the shared wallet.
 */
export const BROCHURE_GENERATE_CREDITS = 0;

/**
 * Credits charged for a new unbranded PDF (the BrochurePDF mark removed).
 * Re-downloading that same PDF is free. A later edit is a new export.
 */
export const BROCHURE_UNBRANDED_CREDITS = 1;

/**
 * Credits charged when an AI style is successfully created.
 * A failed generation is not charged. Standard templates stay free.
 * This fee does not include an unbranded export.
 */
export const BROCHURE_AI_STYLE_CREDITS = 2;

/** Marketing Pack credit cost (window card, social sizes, email flyer, portal images, QR). */
export const BROCHURE_MARKETING_PACK_CREDITS = 2;

const unbrandedUnit = BROCHURE_UNBRANDED_CREDITS === 1 ? 'credit' : 'credits';
const aiStyleUnit = 'credits';

export const BROCHURE_CREDIT_COPY = {
  generate: 'Download branded PDF — free',
  generateShort: 'Free',
  branded: 'Download branded PDF — free',
  brandedShort: 'Free',
  unbranded: `Remove BrochurePDF branding — ${BROCHURE_UNBRANDED_CREDITS} ${unbrandedUnit}`,
  unbrandedConfirm: `Removing the BrochurePDF mark uses ${BROCHURE_UNBRANDED_CREDITS} ReHub ${unbrandedUnit}. You can download this same unbranded PDF again for free.`,
  unbrandedEdited: `You changed the brochure. This is a new unbranded export and uses ${BROCHURE_UNBRANDED_CREDITS} ReHub ${unbrandedUnit}.`,
  repeatUnbranded: 'Download this unbranded PDF again — no extra credit.',
  aiStyle: `Create an AI style — ${BROCHURE_AI_STYLE_CREDITS} ${aiStyleUnit}`,
  aiStyleExplain: `Standard templates and the branded PDF are free. An AI style is optional and costs ${BROCHURE_AI_STYLE_CREDITS} ReHub ${aiStyleUnit}. You are charged only when a design is created. If generation fails, you are not charged. The AI style changes colours, type, cover, photo arrangement and section styling. It does not change your property details or photos. Returning to a standard design is free.`,
  faqAnswer: `Standard branded brochure creation and PDF downloads are free, including repeat downloads. An optional AI style costs ${BROCHURE_AI_STYLE_CREDITS} ReHub ${aiStyleUnit} when a design is created, and nothing if generation fails. Returning to a standard template is free. Removing the BrochurePDF mark is a separate charge of ${BROCHURE_UNBRANDED_CREDITS} ReHub ${unbrandedUnit} for each new unbranded PDF. Downloading that same unbranded PDF again is free. A Marketing Pack uses ${BROCHURE_MARKETING_PACK_CREDITS} credits. Other ReHub tools may use credits. No subscription is required.`,
} as const;

/** Total credits required to process `images` HDR photos. */
export function hdrCreditsForImages(images: number): number {
  return Math.max(0, images) * HDR_CREDIT_COST;
}

/** How many HDR images a given credit balance can finish. */
export function hdrImagesForCredits(credits: number): number {
  if (HDR_CREDIT_COST <= 0) return 0;
  return Math.floor(Math.max(0, credits) / HDR_CREDIT_COST);
}

/**
 * Format a credit amount for display, showing decimals only when present
 * (e.g. 12 → "12", 12.5 → "12.5"). Use everywhere a fractional balance or
 * cost can surface so the UI never rounds a half-credit away.
 */
export function formatCreditAmount(credits: number): string {
  const n = Number(credits) || 0;
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
}

/**
 * Single source of truth for HDR Photo pricing copy. Components should import
 * these instead of hard-coding the per-image cost so a future price change is
 * a one-line edit here (plus the DB helper).
 */
export const HDR_CREDIT_COPY = {
  /** Short marketing line for pricing sections / headers. */
  perImage: 'HDR Photo uses HDR Credits — 10 free edits every month, separate from ReHub Credits',
  /** Compact label for trust strips / badges. */
  perImageShort: '10 HDR Credits / month free',
  /** Longer helper shown near the upload / pricing area. */
  optimisedHelper:
    'Each finished HDR edit uses 1 HDR Credit from your monthly allowance. ReHub Credits are unchanged and stay available for PhotoClear, staging, HyperDusk and ImageMotion.',
  /** FAQ-style answer to "How much does it cost?". */
  faqAnswer:
    'Every account includes 10 HDR Credits per month — enough for 10 free edits. Paid plans add a larger monthly HDR Credit allowance. HDR processing never deducts ReHub Credits — those remain for other ReHub apps.',
} as const;

// =============================================================================
// Shared ReHub credit packs (display catalogue lives in credit-packs.ts)
// =============================================================================

/** Welcome bonus granted by handle_new_user(). Keep in sync with the DB trigger. */
export const WELCOME_CREDITS = 10;

export type ToursSubscriptionTier = 'free' | 'pro' | 'agency' | 'business';
export type ActiveSpacePaidTier = Exclude<ToursSubscriptionTier, 'free'>;
export type ActiveSpaceCurrency = 'GBP' | 'EUR' | 'USD';

export type HostingPlanId = '7d' | '30d' | '6mo' | '12mo' | 'slot';

/** Slot subscription tiers. Active slots are derived from tier in app logic — no Stripe metadata required. */
export const TOUR_SUBSCRIPTION_TIERS: {
  tier: ToursSubscriptionTier;
  name: string;
  /** Display price per month (GBP) */
  monthlyPriceGBP: number;
  /** Display price per month (EUR) — pending Stripe Price IDs */
  monthlyPriceEUR: number;
  /** Display price per month (USD) */
  monthlyPriceUSD: number;
  activeSlotLimit: number;
  /** Bonus credits granted each month on renewal */
  bonusCreditsPerMonth: number;
}[] = [
  { tier: 'free', name: 'Free', monthlyPriceGBP: 0, monthlyPriceEUR: 0, monthlyPriceUSD: 0, activeSlotLimit: 0, bonusCreditsPerMonth: 0 },
  { tier: 'pro', name: 'Pro Solo', monthlyPriceGBP: 29, monthlyPriceEUR: 35, monthlyPriceUSD: 39, activeSlotLimit: 15, bonusCreditsPerMonth: 0 },
  { tier: 'agency', name: 'Agency', monthlyPriceGBP: 69, monthlyPriceEUR: 79, monthlyPriceUSD: 79, activeSlotLimit: 50, bonusCreditsPerMonth: 0 },
  { tier: 'business', name: 'Business', monthlyPriceGBP: 129, monthlyPriceEUR: 155, monthlyPriceUSD: 149, activeSlotLimit: 150, bonusCreditsPerMonth: 0 },
];

type ActiveSpacePriceEnv = Partial<{
  VITE_STRIPE_ACTIVE_SPACE_PRICE_PRO_GBP: string;
  VITE_STRIPE_SLOT_PRICE_PRO_MONTHLY_GBP: string;
  VITE_STRIPE_ACTIVE_SPACE_PRICE_PRO_USD: string;
  VITE_STRIPE_SLOT_PRICE_PRO_MONTHLY_USD: string;
  VITE_STRIPE_ACTIVE_SPACE_PRICE_AGENCY_GBP: string;
  VITE_STRIPE_SLOT_PRICE_AGENCY_MONTHLY_GBP: string;
  VITE_STRIPE_ACTIVE_SPACE_PRICE_AGENCY_USD: string;
  VITE_STRIPE_SLOT_PRICE_AGENCY_MONTHLY_USD: string;
  VITE_STRIPE_ACTIVE_SPACE_PRICE_BUSINESS_GBP: string;
  VITE_STRIPE_SLOT_PRICE_BUSINESS_MONTHLY_GBP: string;
  VITE_STRIPE_ACTIVE_SPACE_PRICE_BUSINESS_USD: string;
  VITE_STRIPE_SLOT_PRICE_BUSINESS_MONTHLY_USD: string;
  VITE_STRIPE_ACTIVE_SPACE_PRICE_PRO_EUR: string;
  VITE_STRIPE_SLOT_PRICE_PRO_MONTHLY_EUR: string;
  VITE_STRIPE_ACTIVE_SPACE_PRICE_AGENCY_EUR: string;
  VITE_STRIPE_SLOT_PRICE_AGENCY_MONTHLY_EUR: string;
  VITE_STRIPE_ACTIVE_SPACE_PRICE_BUSINESS_EUR: string;
  VITE_STRIPE_SLOT_PRICE_BUSINESS_MONTHLY_EUR: string;
}>;

export type ActiveSpaceStripePriceIds = Record<ActiveSpacePaidTier, Record<ActiveSpaceCurrency, string>>;

/**
 * Source-of-truth resolver for active-space Stripe price IDs in the web apps.
 * Pro Solo and Business GBP are historical hardcoded IDs. EUR IDs are live
 * fallbacks; env vars still win when set. USD continues to use env mappings.
 */
export const getActiveSpaceStripePriceIds = (env: ActiveSpacePriceEnv): ActiveSpaceStripePriceIds => ({
  pro: {
    GBP: 'price_1T5KXLDHlg75FZHHtcOlgh9J',
    EUR: env.VITE_STRIPE_ACTIVE_SPACE_PRICE_PRO_EUR || env.VITE_STRIPE_SLOT_PRICE_PRO_MONTHLY_EUR || 'price_1UESlgDHlg75FZHHn3trUdNp',
    USD: env.VITE_STRIPE_ACTIVE_SPACE_PRICE_PRO_USD || env.VITE_STRIPE_SLOT_PRICE_PRO_MONTHLY_USD || '',
  },
  agency: {
    GBP: env.VITE_STRIPE_ACTIVE_SPACE_PRICE_AGENCY_GBP || env.VITE_STRIPE_SLOT_PRICE_AGENCY_MONTHLY_GBP || '',
    EUR: env.VITE_STRIPE_ACTIVE_SPACE_PRICE_AGENCY_EUR || env.VITE_STRIPE_SLOT_PRICE_AGENCY_MONTHLY_EUR || 'price_1UESliDHlg75FZHHfeRNRlgW',
    USD: env.VITE_STRIPE_ACTIVE_SPACE_PRICE_AGENCY_USD || env.VITE_STRIPE_SLOT_PRICE_AGENCY_MONTHLY_USD || '',
  },
  business: {
    GBP: 'price_1T5KWDDHlg75FZHHlmSoy6Iv',
    EUR: env.VITE_STRIPE_ACTIVE_SPACE_PRICE_BUSINESS_EUR || env.VITE_STRIPE_SLOT_PRICE_BUSINESS_MONTHLY_EUR || 'price_1UESliDHlg75FZHHU6vEjAwb',
    USD: env.VITE_STRIPE_ACTIVE_SPACE_PRICE_BUSINESS_USD || env.VITE_STRIPE_SLOT_PRICE_BUSINESS_MONTHLY_USD || '',
  },
});

// Minimal sanity check: each paid plan must define GBP, EUR and USD display prices.
for (const tier of TOUR_SUBSCRIPTION_TIERS) {
  if (tier.tier === 'free') continue;
  if (!Number.isFinite(tier.monthlyPriceGBP) || !Number.isFinite(tier.monthlyPriceEUR) || !Number.isFinite(tier.monthlyPriceUSD)) {
    throw new Error(`Missing active-space display price for tier "${tier.tier}".`);
  }
}

/** Credit-based hosting duration (shared by 360 Tours and Planos). */
export const HOSTING_PLANS: {
  plan: HostingPlanId;
  label: string;
  days: number;
  credits: number;
  badge?: 'free' | 'most-popular' | 'best-value';
}[] = [
  { plan: '7d', label: '7 Days', days: 7, credits: 2, badge: 'free' },
  { plan: '30d', label: '30 Days', days: 30, credits: 3 },
  { plan: '12mo', label: '1 Year', days: 365, credits: 10, badge: 'best-value' },
  { plan: '6mo', label: '6 Months', days: 180, credits: 6, badge: 'most-popular' },
];

// =============================================================================
// Doorsteps subscription tiers
// =============================================================================

export type DoorstepsTier = 'free' | 'starter' | 'pro' | 'agency';
export type DoorstepsBillingCycle = 'monthly' | 'annual';

/**
 * Locked tiers per `doorsteps-pricing.md`. Mail margin baseline is £0.98/letter
 * (Stannp A5 enveloped at ~1,200 volume on Starter plan, April 2026).
 */
export const DOORSTEPS_TIERS: {
  tier: DoorstepsTier;
  name: string;
  /** Display price per month, GBP whole pounds. */
  monthlyPriceGBP: number;
  /** Display price per year, GBP whole pounds (= 10 months). */
  annualPriceGBP: number;
  /** Per-letter rate the agent pays, in pence. 0 means cannot send. */
  perLetterPence: number;
  /** One-line marketing summary. */
  blurb: string;
  /** Hard limits enforced in the app. */
  limits: {
    savedTerritories: number | 'unlimited';
    activeBranches: number | 'unlimited';
    seats: number | 'unlimited';
    canSendCampaigns: boolean;
    canAccessPipeline: boolean;
    multiTemplate: boolean;
    abTesting: boolean;
    perBranchLeadRouting: boolean;
    customSubdomain: boolean;
    apiAccess: boolean;
    prioritySupport: boolean;
  };
  /** Marketing-facing feature highlights, ordered. */
  highlights: string[];
}[] = [
  {
    tier: 'free',
    name: 'Free',
    monthlyPriceGBP: 0,
    annualPriceGBP: 0,
    perLetterPence: 0,
    blurb: 'Trial the targeting, see who you could be writing to.',
    limits: {
      savedTerritories: 1,
      activeBranches: 1,
      seats: 1,
      canSendCampaigns: false,
      canAccessPipeline: false,
      multiTemplate: false,
      abTesting: false,
      perBranchLeadRouting: false,
      customSubdomain: false,
      apiAccess: false,
      prioritySupport: false,
    },
    highlights: [
      'Build 1 territory',
      'Preview up to 50 matching properties',
      'See comparables and signal scores',
      'Sending disabled — upgrade to dispatch',
    ],
  },
  {
    tier: 'starter',
    name: 'Starter',
    monthlyPriceGBP: 49,
    annualPriceGBP: 490,
    perLetterPence: 125,
    blurb: 'Solo agents testing prospecting campaigns.',
    limits: {
      savedTerritories: 5,
      activeBranches: 1,
      seats: 1,
      canSendCampaigns: true,
      canAccessPipeline: true,
      multiTemplate: false,
      abTesting: false,
      perBranchLeadRouting: false,
      customSubdomain: false,
      apiAccess: false,
      prioritySupport: false,
    },
    highlights: [
      '5 saved territories',
      'Unlimited campaigns at £1.25 / letter',
      'Branded landing pages + lead pipeline',
      'Add Valmate Pro for £19/mo (vs £49 standalone)',
    ],
  },
  {
    tier: 'pro',
    name: 'Pro',
    monthlyPriceGBP: 99,
    annualPriceGBP: 990,
    perLetterPence: 115,
    blurb: 'Active prospectors, 100–500 letters / month.',
    limits: {
      savedTerritories: 25,
      activeBranches: 1,
      seats: 1,
      canSendCampaigns: true,
      canAccessPipeline: true,
      multiTemplate: true,
      abTesting: true,
      perBranchLeadRouting: false,
      customSubdomain: false,
      apiAccess: false,
      prioritySupport: true,
    },
    highlights: [
      '25 saved territories',
      'Unlimited campaigns at £1.15 / letter',
      'Multi-template + A/B testing',
      'Valmate Pro included free (£49 value)',
      'Priority support',
    ],
  },
  {
    tier: 'agency',
    name: 'Agency',
    monthlyPriceGBP: 249,
    annualPriceGBP: 2490,
    perLetterPence: 105,
    blurb: 'Multi-branch, 500+ letters / month.',
    limits: {
      savedTerritories: 'unlimited',
      activeBranches: 'unlimited',
      seats: 5,
      canSendCampaigns: true,
      canAccessPipeline: true,
      multiTemplate: true,
      abTesting: true,
      perBranchLeadRouting: true,
      customSubdomain: true,
      apiAccess: true,
      prioritySupport: true,
    },
    highlights: [
      'Unlimited territories + branches',
      'Unlimited campaigns at £1.05 / letter',
      'Per-branch lead routing',
      'Custom subdomain + API access',
      'Valmate Pro included free for the agency',
    ],
  },
];

export function getDoorstepsTier(tier: string | null | undefined): typeof DOORSTEPS_TIERS[number] {
  return DOORSTEPS_TIERS.find((t) => t.tier === (tier ?? 'free')) ?? DOORSTEPS_TIERS[0];
}

export function getDoorstepsLetterRatePence(tier: string | null | undefined): number {
  return getDoorstepsTier(tier).perLetterPence;
}

/**
 * Stripe price IDs for each tier × billing cycle. Sourced from env vars so
 * we can keep prod/staging price IDs separate. Set via Vercel project env
 * (and supabase secrets for the checkout edge function).
 *
 * Naming: VITE_STRIPE_PRICE_DOORSTEPS_<TIER>_<CYCLE>_GBP
 *   e.g. VITE_STRIPE_PRICE_DOORSTEPS_PRO_MONTHLY_GBP
 */
export type DoorstepsStripePriceIds = Record<
  Exclude<DoorstepsTier, 'free'>,
  Record<DoorstepsBillingCycle, string>
>;

type DoorstepsPriceEnv = Partial<Record<string, string>>;

export const getDoorstepsStripePriceIds = (env: DoorstepsPriceEnv): DoorstepsStripePriceIds => ({
  starter: {
    monthly: env.VITE_STRIPE_PRICE_DOORSTEPS_STARTER_MONTHLY_GBP || env.STRIPE_PRICE_DOORSTEPS_STARTER_MONTHLY_GBP || '',
    annual:  env.VITE_STRIPE_PRICE_DOORSTEPS_STARTER_ANNUAL_GBP  || env.STRIPE_PRICE_DOORSTEPS_STARTER_ANNUAL_GBP  || '',
  },
  pro: {
    monthly: env.VITE_STRIPE_PRICE_DOORSTEPS_PRO_MONTHLY_GBP || env.STRIPE_PRICE_DOORSTEPS_PRO_MONTHLY_GBP || '',
    annual:  env.VITE_STRIPE_PRICE_DOORSTEPS_PRO_ANNUAL_GBP  || env.STRIPE_PRICE_DOORSTEPS_PRO_ANNUAL_GBP  || '',
  },
  agency: {
    monthly: env.VITE_STRIPE_PRICE_DOORSTEPS_AGENCY_MONTHLY_GBP || env.STRIPE_PRICE_DOORSTEPS_AGENCY_MONTHLY_GBP || '',
    annual:  env.VITE_STRIPE_PRICE_DOORSTEPS_AGENCY_ANNUAL_GBP  || env.STRIPE_PRICE_DOORSTEPS_AGENCY_ANNUAL_GBP  || '',
  },
});

/** Credit-based hosting duration for Showcase (higher credit costs than Tours/Planos). */
export const SHOWCASE_HOSTING_PLANS: {
  plan: HostingPlanId;
  label: string;
  days: number;
  credits: number;
  badge?: 'most-popular' | 'best-value';
}[] = [
  { plan: '7d', label: '7 Days', days: 7, credits: 3 },
  { plan: '30d', label: '30 Days', days: 30, credits: 8, badge: 'most-popular' },
  { plan: '6mo', label: '6 Months', days: 180, credits: 18 },
  { plan: '12mo', label: '12 Months', days: 365, credits: 25, badge: 'best-value' },
];
